import type { KpiEntry } from "@/api/kpi";
import type { ColumnDef } from "@/components/SortableTable";
import { DeepLink } from "@/components/DeepLink";
import { orderStatusLabel } from "@/lib/labels";
import { t } from "@/i18n";
import { fmtDateTime, fmtInt, fmtMoney } from "@/lib/utils";

/**
 * SortableTable kolon tanimlari — legacy `frontend/legacy/js/modals.js`
 * `columnsFor()` (satir 107-238) portu. `/kpi/{kpi_id}/items` artik loader'in
 * HAM satirlarina EK OLARAK `app/core/api/kpi.py` `_annotate_sla`'nin
 * ekledigi turemis alanlari da tasir: `level` (kargolanmamis/kargo_yolda/
 * destek_talepleri/bugun_destek), `working_hours` (kargolanmamis/kargo_yolda),
 * `wait_hours` (destek_talepleri/bugun_destek), `admin_link` (order/ticket/
 * product turundeki kartlar — bkz. backend `_ADMIN_LINK_SPEC`), `rank`
 * (encok_satan). Bu dosya bu alanlari legacy ile ayni sutun/rozet/renk
 * kurallarina gore isler.
 *
 * legacy'de OLMAYAN (dolayisiyla burada da eklenmeyen) kolonlar: odeme_bekleyen/
 * bugunku_siparis/bekleyen_iade icin Seviye/sure (columnsFor satir 203-211'de
 * yok). bekleyen_iade icin admin_link ARTIK VAR (kullanici karari 10.09.2026:
 * iade satirindaki siparis icin de Ticimax derin baglantisi — bkz.
 * `loaders.load_returns`'un ekledigi `order_id` + backend `_ADMIN_LINK_SPEC`).
 */

const s = (v: unknown): string => (v == null ? "" : String(v));
const n = (v: unknown): number => {
  const num = Number(v);
  return Number.isFinite(num) ? num : 0;
};
const mono = (v: unknown) => <span className="font-mono text-xs">{s(v) || "—"}</span>;
const dateCell = (v: unknown) => (v ? fmtDateTime(String(v)) : "—");

function adminLinkOf(r: KpiEntry): string | null {
  return typeof r.admin_link === "string" && r.admin_link ? r.admin_link : null;
}

/** Sipariş no / talep no — legacy `tcxLink` (satır 131,158,183) portu: admin_link varsa Ticimax'a link, yoksa düz mono metin. */
function codeLinkCell(value: unknown, r: KpiEntry) {
  return <DeepLink href={adminLinkOf(r)} label={s(value) || "—"} mono />;
}

// ---------------------------------------------------------------------------
// Seviye rozeti — legacy `lvlPill` (satır 109-112). Legacy `lvl` degerini
// CEVRILMEDEN (ham "critical"/"warning"/"ok") basiyordu; burada Turkce'ye
// cevrilir (rozet metni t() ile) — geri kalan UI zaten hep Turkce.
// ---------------------------------------------------------------------------

const LEVEL_SIRA: Record<string, number> = { critical: 0, warning: 1, ok: 2 };
const LEVEL_ETIKET_KEY: Record<string, string> = {
  critical: "kpi.seviye.kritik",
  warning: "kpi.seviye.uyari",
  ok: "kpi.seviye.ok",
};
const LEVEL_RENK: Record<string, string> = {
  critical: "bg-red-pale text-red",
  warning: "bg-amber-pale text-amber",
  ok: "bg-green-pale text-green",
};

function levelBadge(level: unknown) {
  const lvl = typeof level === "string" ? level : null;
  if (!lvl || !(lvl in LEVEL_RENK)) return <span className="text-muted">—</span>;
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${LEVEL_RENK[lvl]}`}>
      {t(LEVEL_ETIKET_KEY[lvl])}
    </span>
  );
}

function levelColumn(): ColumnDef<KpiEntry> {
  return {
    key: "level",
    header: "Seviye",
    accessor: (r) => levelBadge(r.level),
    sortValue: (r) => LEVEL_SIRA[String(r.level)] ?? 99,
  };
}

// ---------------------------------------------------------------------------
// Süre bicimi — legacy `fmtDuration` (satır 116-126) BIREBIR port: "6 dk" /
// "1 sa 30 dk" / "2 gün 4 sa". 0/negatif -> "—" (kargolanmamis/kargo_yolda
// icin "mesai dışı" ozel durumu ayrica `gecenSureCell`'de ele alinir).
// ---------------------------------------------------------------------------

function fmtDuration(hours: unknown): string {
  const h = n(hours);
  if (h <= 0) return "—";
  const totalMin = Math.round(h * 60);
  if (totalMin < 60) return `${totalMin} dk`;
  const days = Math.floor(totalMin / 1440);
  const remH = Math.floor((totalMin % 1440) / 60);
  const remM = totalMin % 60;
  if (days > 0) return remH > 0 ? `${days} gün ${remH} sa` : `${days} gün`;
  return remM > 0 ? `${remH} sa ${remM} dk` : `${remH} sa`;
}

/** "Geçen Süre (mesai)" hücresi — legacy `gecenSureCell` (satır 142-150): 0 saatlik satırlar "mesai dışı verildi" ipucuyla ayrı gösterilir. */
function gecenSureCell(r: KpiEntry) {
  if (r.working_hours == null) return <span className="text-xs text-muted">—</span>;
  const wh = n(r.working_hours);
  if (wh <= 0) {
    const created = r.created_at ? fmtDateTime(String(r.created_at)) : "";
    return (
      <span
        className="text-xs text-muted"
        title={`Sipariş mesai saatleri dışında verildi (${created}) — mesai başlayınca süre işlemeye başlar`}
      >
        0 sa <span className="opacity-70">({t("kpi.sure.mesai_disi")})</span>
      </span>
    );
  }
  return <span>{fmtDuration(wh)}</span>;
}

/** "Bekleme" hücresi (destek_talepleri/bugun_destek `wait_hours`) — legacy satır 188. */
function beklemeCell(r: KpiEntry) {
  return <span>{fmtDuration(r.wait_hours)}</span>;
}

function siparisColumns(opts: { sla?: boolean } = {}): ColumnDef<KpiEntry>[] {
  const cols: ColumnDef<KpiEntry>[] = [
    { key: "order_no", header: "Sipariş", accessor: (r) => codeLinkCell(r.order_no, r) },
    { key: "customer_name", header: "Müşteri", accessor: (r) => s(r.customer_name) || "—" },
    {
      key: "amount",
      header: "Tutar",
      accessor: (r) => fmtMoney(n(r.amount)),
      sortValue: (r) => n(r.amount),
    },
    { key: "created_at", header: "Tarih", accessor: (r) => dateCell(r.created_at), sortValue: (r) => s(r.created_at) },
    { key: "status", header: "Durum", accessor: (r) => orderStatusLabel(s(r.status)) },
    { key: "cargo_company", header: "Kargo Şirketi", accessor: (r) => s(r.cargo_company) || "—" },
  ];
  // legacy columnsFor satır 137-178: yalnız kargolanmamis/kargo_yolda "Geçen
  // Süre (mesai)" + "Seviye" alır — odeme_bekleyen/bugunku_siparis'te YOK.
  if (opts.sla) {
    cols.push(
      {
        key: "working_hours",
        header: "Geçen Süre (mesai)",
        accessor: gecenSureCell,
        sortValue: (r) => n(r.working_hours),
      },
      levelColumn(),
    );
  }
  return cols;
}

/** legacy satır 169-176: SLA satır rengi YALNIZ kargolanmamis modalinde vardı
 * (kargo_yolda/destek_talepleri/bugun_destek'te Seviye rozeti var ama satır
 * rengi YOK) — eşikler de rozetteki esik_saat'ten BAĞIMSIZ, sabit (working_hours
 * > 8 sa kritik, > 4 sa uyarı). `KpiDetailModal` bunu `SortableTable`'ın
 * `rowClassName` prop'una geçirir. */
export function rowClassFor(kpiId: string): ((row: KpiEntry) => string | undefined) | undefined {
  if (kpiId !== "kargolanmamis") return undefined;
  return (r) => {
    const wh = n(r.working_hours);
    if (wh > 8) return "bg-red-pale";
    if (wh > 4) return "bg-amber-pale";
    return "bg-green-pale";
  };
}

function destekColumns(): ColumnDef<KpiEntry>[] {
  return [
    { key: "ticket_id", header: "ID", accessor: (r) => codeLinkCell(r.ticket_id, r) },
    { key: "subject", header: "Konu", accessor: (r) => s(r.subject) || "—" },
    { key: "customer_name", header: "Müşteri", accessor: (r) => s(r.customer_name) || "—" },
    { key: "created_at", header: "Açılış", accessor: (r) => dateCell(r.created_at), sortValue: (r) => s(r.created_at) },
    { key: "first_response_at", header: "İlk yanıt", accessor: (r) => dateCell(r.first_response_at) },
    { key: "wait_hours", header: "Bekleme", accessor: beklemeCell, sortValue: (r) => n(r.wait_hours) },
    { key: "status", header: "Durum", accessor: (r) => orderStatusLabel(s(r.status)) },
    levelColumn(),
  ];
}

function iadeColumns(): ColumnDef<KpiEntry>[] {
  return [
    { key: "return_id", header: "İade ID", accessor: (r) => mono(r.return_id) },
    // Kullanıcı kararı (10.09.2026): iade satırındaki sipariş için de Ticimax
    // derin bağlantısı olsun — `loaders.load_returns` artık satıra siparişin
    // platform native id'sini `order_id` alanına ekliyor (`_ADMIN_LINK_SPEC`
    // "bekleyen_iade" → ("order", "order_id")); eşleşme yoksa backend
    // `admin_link`'i null döner ve `codeLinkCell` (siparisColumns ile aynı
    // desen) düz mono metne düşer.
    { key: "order_no", header: "Sipariş", accessor: (r) => codeLinkCell(r.order_no, r) },
    { key: "customer_name", header: "Müşteri", accessor: (r) => s(r.customer_name) || "—" },
    { key: "amount", header: "Tutar", accessor: (r) => fmtMoney(n(r.amount)), sortValue: (r) => n(r.amount) },
    {
      key: "created_at",
      header: "Talep Tarihi",
      accessor: (r) => dateCell(r.created_at),
      sortValue: (r) => s(r.created_at),
    },
    { key: "reason", header: "Sebep", accessor: (r) => s(r.reason) || "—" },
    { key: "status", header: "Durum", accessor: (r) => orderStatusLabel(s(r.status)) },
  ];
}

function starsCell(rating: unknown): string {
  const r = Math.max(0, Math.min(5, Math.round(n(rating))));
  return r ? "★".repeat(r) + "☆".repeat(5 - r) : "—";
}

function yorumColumns(): ColumnDef<KpiEntry>[] {
  return [
    { key: "created_at", header: "Tarih", accessor: (r) => dateCell(r.created_at), sortValue: (r) => s(r.created_at) },
    { key: "customer_name", header: "Müşteri", accessor: (r) => s(r.customer_name) || "—" },
    { key: "rating", header: "Puan", accessor: (r) => starsCell(r.rating), sortValue: (r) => n(r.rating) },
    {
      key: "product_name",
      header: "Ürün",
      accessor: (r) => (
        <>
          <div className="font-medium">{s(r.product_name) || "—"}</div>
          {r.sku ? <div className="font-mono text-[11px] text-muted">{s(r.sku)}</div> : null}
        </>
      ),
    },
    {
      key: "body",
      header: "Yorum",
      // legacy satır 35/220-225: "✓ Satın aldı" rozeti (`is_verified_purchase`)
      // — mevcut `yorumlar.satin_aldi` i18n anahtarı + YorumKart.tsx ile aynı stil.
      accessor: (r) => (
        <div className="flex flex-wrap items-center gap-2">
          <span>{s(r.body).slice(0, 240) || "—"}</span>
          {r.is_verified_purchase ? (
            <span className="rounded-full bg-green-pale px-2 py-0.5 text-xs font-medium text-green">
              {t("yorumlar.satin_aldi")}
            </span>
          ) : null}
        </div>
      ),
    },
  ];
}

function yeniUyeColumns(): ColumnDef<KpiEntry>[] {
  return [
    { key: "date", header: "Tarih", accessor: (r) => s(r.date), sortValue: (r) => s(r.date) },
    {
      key: "new_member_count",
      header: "Yeni üye",
      accessor: (r) => fmtInt(n(r.new_member_count)),
      sortValue: (r) => n(r.new_member_count),
    },
  ];
}

function encokSatanColumns(): ColumnDef<KpiEntry>[] {
  return [
    // legacy satır 412: "#" sıra no — backend `_items_encok_satan` artık
    // gerçekten dolduruyor (legacy'de `kpi_encok_satan` bu alanı hiç
    // doldurmuyordu, kolon her zaman boştu).
    { key: "rank", header: "#", accessor: (r) => s(r.rank) || "—", sortValue: (r) => n(r.rank) },
    {
      key: "name",
      header: "Ürün",
      accessor: (r) => (
        <>
          <div className="font-medium">{s(r.name) || "—"}</div>
          {r.sku ? <div className="font-mono text-[11px] text-muted">{s(r.sku)}</div> : null}
        </>
      ),
    },
    { key: "adet", header: "Adet", accessor: (r) => fmtInt(n(r.adet)), sortValue: (r) => n(r.adet) },
    { key: "ciro", header: "Ciro", accessor: (r) => fmtMoney(n(r.ciro)), sortValue: (r) => n(r.ciro) },
  ];
}

function saatlikSatisColumns(): ColumnDef<KpiEntry>[] {
  return [
    { key: "hour", header: "Saat", accessor: (r) => s(r.hour) },
    { key: "today_orders", header: "Bugün sipariş", accessor: (r) => fmtInt(n(r.today_orders)) },
    { key: "today_revenue", header: "Bugün ciro", accessor: (r) => fmtMoney(n(r.today_revenue)) },
    { key: "yesterday_orders", header: "Dün sipariş", accessor: (r) => fmtInt(n(r.yesterday_orders)) },
    { key: "yesterday_revenue", header: "Dün ciro", accessor: (r) => fmtMoney(n(r.yesterday_revenue)) },
  ];
}

/** Kritik stok — tek VARYANT satiri (urun karti gruplamasi icin bkz. stockGroup.ts). */
export function kritikStokVariantColumns(): ColumnDef<KpiEntry>[] {
  return [
    { key: "beden", header: "Beden", accessor: (r) => s(r.beden) || s(r.varyasyon) || "—" },
    { key: "sku", header: "SKU", accessor: (r) => mono(r.sku) },
    { key: "stock", header: "Stok", accessor: (r) => fmtInt(n(r.stock)), sortValue: (r) => n(r.stock) },
    { key: "fiyat", header: "Fiyat", accessor: (r) => fmtMoney(n(r.fiyat)), sortValue: (r) => n(r.fiyat) },
    { key: "status", header: "Durum", accessor: (r) => orderStatusLabel(s(r.status)) },
  ];
}

const COLUMN_MAP: Record<string, () => ColumnDef<KpiEntry>[]> = {
  kargolanmamis: () => siparisColumns({ sla: true }),
  kargo_yolda: () => siparisColumns({ sla: true }),
  odeme_bekleyen: () => siparisColumns(),
  bugunku_siparis: () => siparisColumns(),
  destek_talepleri: destekColumns,
  bugun_destek: destekColumns,
  bekleyen_iade: iadeColumns,
  musteri_yorumlari: yorumColumns,
  yeni_uye: yeniUyeColumns,
  encok_satan: encokSatanColumns,
  saatlik_satis: saatlikSatisColumns,
  kritik_stok: kritikStokVariantColumns,
};

/** kpi_id → kolon tanimlari; eslesme yoksa null (cagiran genel bir dokum gosterebilir). */
export function columnsFor(kpiId: string): ColumnDef<KpiEntry>[] | null {
  return COLUMN_MAP[kpiId]?.() ?? null;
}
